import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { SecondaryMarketOrdersPresenter } from './secondary-market-orders-presenter'
import {
  secondaryMarketServices,
  SecondaryMarketOrder,
  SecondaryMarketSettings,
} from '../../services/secondary-market-service'
import { showBanner } from '../../global-state/banner-slice'

export const SecondaryMarketOrdersContainer = () => {
  const dispatch = useDispatch()
  const [orders, setOrders] = useState<SecondaryMarketOrder[]>([])
  const [settings, setSettings] = useState<SecondaryMarketSettings>({
    is_open: false,
    window_label: '',
    closes_at: null,
  })
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [matchModalOpen, setMatchModalOpen] = useState(false)
  const [sellOrderId, setSellOrderId] = useState<number | null>(null)
  const [buyOrderId, setBuyOrderId] = useState<number | null>(null)
  const [agreedPrice, setAgreedPrice] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [ordersRes, settingsRes] = await Promise.all([
        secondaryMarketServices.getOrders({
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(typeFilter ? { type: typeFilter } : {}),
          limit: 100,
        }),
        secondaryMarketServices.getSettings(),
      ])
      if (ordersRes.status === 200) setOrders(ordersRes.orders)
      if (settingsRes.status === 200) setSettings(settingsRes.settings)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, typeFilter])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleToggleWindow = async () => {
    const res = await secondaryMarketServices.updateSettings({
      is_open: !settings.is_open,
      window_label: settings.window_label || undefined,
    })
    if (res.status === 200) {
      setSettings(res.settings)
      dispatch(showBanner({ text: 'Trading window updated', variant: 'success-banner' }))
      loadData()
    }
  }

  const handleSaveWindowLabel = async (label: string) => {
    const res = await secondaryMarketServices.updateSettings({
      is_open: settings.is_open,
      window_label: label,
    })
    if (res.status === 200) {
      setSettings(res.settings)
      dispatch(showBanner({ text: 'Window label saved', variant: 'success-banner' }))
    }
  }

  const handleMatch = async () => {
    if (!sellOrderId || !buyOrderId || !agreedPrice) return
    const res = await secondaryMarketServices.matchOrders({
      sell_order_id: sellOrderId,
      buy_order_id: buyOrderId,
      agreed_unit_price: Number(agreedPrice),
    })
    if (res.status === 200) {
      dispatch(showBanner({ text: 'Orders matched', variant: 'success-banner' }))
      setMatchModalOpen(false)
      setSellOrderId(null)
      setBuyOrderId(null)
      setAgreedPrice('')
      loadData()
    }
  }

  const handleStatusUpdate = async (orderId: number, status: string) => {
    const res = await secondaryMarketServices.updateOrderStatus(orderId, status)
    if (res.status === 200) {
      const text = status === 'OPEN' ? 'Orders unmatched' : `Order marked ${status}`
      dispatch(showBanner({ text, variant: 'success-banner' }))
      loadData()
    }
  }

  const openSellOrders = orders.filter((o) => o.type === 'SELL' && o.status === 'OPEN')
  const openBuyOrders = orders.filter((o) => o.type === 'BUY_INTEREST' && o.status === 'OPEN')

  const handleOpenMatchModal = (prefill?: {
    sellOrderId?: number | null
    buyOrderId?: number | null
    agreedPrice?: string
  }) => {
    setSellOrderId(prefill?.sellOrderId ?? null)
    setBuyOrderId(prefill?.buyOrderId ?? null)
    setAgreedPrice(prefill?.agreedPrice ?? '')
    setMatchModalOpen(true)
  }

  return (
    <SecondaryMarketOrdersPresenter
      orders={orders}
      settings={settings}
      loading={loading}
      statusFilter={statusFilter}
      typeFilter={typeFilter}
      matchModalOpen={matchModalOpen}
      sellOrderId={sellOrderId}
      buyOrderId={buyOrderId}
      agreedPrice={agreedPrice}
      openSellOrders={openSellOrders}
      openBuyOrders={openBuyOrders}
      onStatusFilterChange={setStatusFilter}
      onTypeFilterChange={setTypeFilter}
      onToggleWindow={handleToggleWindow}
      onSaveWindowLabel={handleSaveWindowLabel}
      onOpenMatchModal={handleOpenMatchModal}
      onCloseMatchModal={() => setMatchModalOpen(false)}
      onSellOrderIdChange={setSellOrderId}
      onBuyOrderIdChange={setBuyOrderId}
      onAgreedPriceChange={setAgreedPrice}
      onMatch={handleMatch}
      onStatusUpdate={handleStatusUpdate}
    />
  )
}
